import * as React from 'react';
import { Form, Col, Row, Button } from 'react-bootstrap';
import { Client } from '../../models';
import { ClientEntryStore } from '../../stores/client-entry-store';
import {
  MarkdownToolbar,
  renderNoteBody,
  wrapSelection,
  formatMarkerForKeydown,
} from './note-markdown';
import { scrollPanelIntoView } from '../../utils/scrollPanelIntoView';

/**
 * Which client-level field this editor is bound to.
 *
 * `overallPractitionerNotes` is the CLIENT-FACING one, despite the name: it
 * prints at the end of the comparison report. `overallPractitionerPrivateNotes`
 * is the practitioner's own half and never prints anywhere.
 */
export type ClientNotesField =
  | 'overallPractitionerNotes'
  | 'overallPractitionerPrivateNotes';

type Props = {
  title: string;
  client: Client;
  clientEntryStore: ClientEntryStore;
  /** Defaults to the client-facing field, which is what every existing caller means. */
  field?: ClientNotesField;
  // When this changes to a new non-empty value, the AI-generated draft replaces
  // the textarea contents (an editable draft — the practitioner reviews + Saves).
  aiDraft?: string;
  // Element id to scroll back to after Save (the enclosing card, so its header
  // stays in view). Without it the field's own top is used.
  scrollAnchorId?: string;
};

type State = {
  text: string;
  // Same preview ⇄ edit shape as the Notes tab, the Insights panel and the
  // consolidated Section Notes panel: the saved note reads FORMATTED, and the
  // raw markup only shows while editing.
  editing: boolean;
};

export class ClientNotes extends React.Component<Props, State> {
  /** The bound field, defaulting to the client-facing one. */
  private get field(): ClientNotesField {
    return this.props.field || 'overallPractitionerNotes';
  }

  private savedText(client: Client): string {
    return client[this.field] || '';
  }

  state: State = {
    text: this.savedText(this.props.client),
    // Nothing saved yet → nothing to preview, so open straight in the editor.
    editing: !this.savedText(this.props.client).trim(),
  };

  private wrapperEl: HTMLDivElement | null = null;
  // The whole field (preview or editor) — the scroll anchor after a save.
  private rootEl: HTMLDivElement | null = null;
  // Roughly the 13 rows this box used to be fixed at — it grows past this with
  // the note, but never shrinks below it.
  private static MIN_HEIGHT_PX = 320;

  componentDidMount() {
    this.fitToContent();
  }

  setWrapperRef = (el: HTMLDivElement | null) => {
    this.wrapperEl = el;
  };

  setRootRef = (el: HTMLDivElement | null) => {
    this.rootEl = el;
  };

  // The save runs through the app's spinner modal, which locks body scrolling —
  // anything scrolled underneath it is clamped and the page lands at the top of
  // the report when it closes. Scroll back once the save has resolved.
  private scrollBackToNotes = () => {
    scrollPanelIntoView(this.props.scrollAnchorId || this.rootEl);
  };

  private getTextarea = (): HTMLTextAreaElement | null =>
    (this.wrapperEl?.querySelector('textarea') as HTMLTextAreaElement | null) ?? null;

  // Grow the box to whatever was typed, so a long note is read by scrolling the
  // page rather than a scrollbar inside the box.
  fitToContent = () => {
    const el = this.getTextarea();
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight, ClientNotes.MIN_HEIGHT_PX)}px`;
    el.style.overflow = 'hidden';
  };

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.client.id !== this.props.client.id) {
      const text = this.savedText(nextProps.client);
      this.setState({ text, editing: !text.trim() }, this.fitToContent);
    }
    // A freshly generated AI draft replaces the textarea (not auto-saved) — it
    // arrives to be reviewed, so open the editor with it.
    if (nextProps.aiDraft && nextProps.aiDraft !== this.props.aiDraft) {
      this.setState(
        { text: nextProps.aiDraft, editing: true },
        this.fitToContent,
      );
    }
  }

  handleInputChange = (event: any) => {
    const value = event.target.value;
    this.setState({ text: value }, this.fitToContent);
  };

  // Restore the selection the toolbar/shortcut asked for once React has
  // re-rendered the (controlled) textarea.
  private applyFormat = (value: string, selStart: number, selEnd: number) => {
    this.setState({ text: value }, () => {
      this.fitToContent();
      const el = this.getTextarea();
      if (el) {
        requestAnimationFrame(() => {
          el.focus();
          el.setSelectionRange(selStart, selEnd);
        });
      }
    });
  };

  private handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const marker = formatMarkerForKeydown(e);
    const el = this.getTextarea();
    if (marker && el) {
      e.preventDefault();
      const { value, selStart, selEnd } = wrapSelection(el, marker);
      this.applyFormat(value, selStart, selEnd);
    }
  };

  handleEdit = () => {
    this.setState({ editing: true }, this.fitToContent);
  };

  // Discard anything typed since Edit by re-reading the saved note.
  handleCancel = () => {
    this.setState({
      text: this.savedText(this.props.client),
      editing: false,
    });
  };

  saveOrUpdate = (event: any) => {
    const { client, clientEntryStore } = this.props;
    event.preventDefault();
    client[this.field] = this.state.text;

    // Ensure the current client in store is this one before saving,
    // or just set it as current client and save.
    clientEntryStore.setCurrentClient(client);
    const saved = clientEntryStore.saveOrUpdate();
    if (saved && typeof saved.then === 'function') {
      saved.then(this.scrollBackToNotes).catch(this.scrollBackToNotes);
    } else {
      this.scrollBackToNotes();
    }
    // Back to the formatted preview, so Save doubles as "show me how this reads".
    this.setState({ editing: false });
  };

  render() {
    const { title } = this.props;
    const { editing, text } = this.state;

    if (!editing) {
      return (
        <div ref={this.setRootRef}>
          {/* Grows with the note — nothing to drag open to read it. */}
          <div
            style={{
              border: '1px solid #ced4da',
              borderRadius: 4,
              padding: 12,
              backgroundColor: '#fff',
            }}
          >
            {text.trim()
              ? renderNoteBody(text)
              : <span style={{ color: '#6c757d' }}>No notes yet.</span>}
          </div>
          <div style={{ marginTop: 10 }}>
            <Button type="button" onClick={this.handleEdit} title="Edit these notes">
              Edit
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div ref={this.setRootRef}>
      <Form id="client-notes-form">
        <Form.Group controlId="clientForm.ControlTextarea1">
          <Form.Label>{title}</Form.Label>
          <MarkdownToolbar
            getTextarea={this.getTextarea}
            onApply={this.applyFormat}
          />
          <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: 6 }}>
            Formatting:&nbsp;<code>## Section</code>&nbsp;for section
            headings,&nbsp;<code>**bold**</code>,&nbsp;<code>*italic*</code>,&nbsp;
            <code>_underline_</code>.&nbsp;These render formatted when you Save.
          </div>
          <div ref={this.setWrapperRef}>
            <Form.Control
              as="textarea"
              rows={13}
              name="notes-text"
              value={text}
              onChange={this.handleInputChange}
              onKeyDown={this.handleKeyDown}
              style={{ overflow: 'hidden', resize: 'none' }}
            />
          </div>
        </Form.Group>
        <Form.Group as={Row}>
          <Col style={{ display: 'flex', gap: 8 }}>
            <Button id="submitButton" type="submit" onClick={this.saveOrUpdate}>
              Save
            </Button>
            <Button type="button" variant="secondary" onClick={this.handleCancel}>
              Cancel
            </Button>
          </Col>
        </Form.Group>
      </Form>
      </div>
    );
  }
}
